import { useState, useEffect, useRef, useCallback } from "react";
import { Mail, Smartphone, MessageCircle, Camera, MessageSquare, Tag } from "lucide-react";
import { useApi } from "@/hooks/use-api";
import { useOrganizationMembers } from "@/hooks/use-organization-members";
import type { ReactNode } from "react";
import { formatTime } from "@/lib/i18n";

interface Conversation {
  id: string;
  personName: string;
  channel: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  status: string;
  assignedTo: string | null;
  tags: string[];
}

interface ConversationTagDefinition {
  name: string;
  color?: string;
}

type TeamMember = {
  id: string;
  name: string | null;
  email: string;
};

interface ConversationListProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  onArchive?: (id: string) => void;
  filters: { status?: string; channel?: string; assignee?: string; tag?: string; search?: string };
  onFiltersChange: (filters: ConversationListProps["filters"]) => void;
  refreshKey?: number;
  optimisticReadConversationIds?: string[];
  enableMobileSwipe?: boolean;
}

const CHANNEL_ICONS: Record<string, ReactNode> = {
  whatsapp: <MessageCircle className="w-4 h-4" />,
  sms: <Smartphone className="w-4 h-4" />,
  email: <Mail className="w-4 h-4" />,
  instagram_dm: <Camera className="w-4 h-4" />,
  facebook_messenger: <MessageSquare className="w-4 h-4" />,
};

const CHANNEL_COLORS: Record<string, string> = {
  whatsapp: "bg-[var(--color-success)]/10 text-[var(--color-success)]", sms: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
  email: "bg-[var(--color-muted)] text-[var(--color-foreground)]", instagram_dm: "bg-[var(--color-ai)]/10 text-[var(--color-ai)]",
  facebook_messenger: "bg-[var(--color-primary)]/10 text-[var(--color-primary)]",
};

const CHANNEL_FILTERS = [
  { id: "email", label: "Email" },
  { id: "sms", label: "SMS" },
  { id: "whatsapp", label: "WhatsApp" },
  { id: "telegram", label: "Telegram" },
  { id: "instagram_dm", label: "Instagram" },
  { id: "facebook_messenger", label: "Messenger" },
] as const;

const TAG_COLORS: Record<string, string> = {
  urgent: "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]", vip: "bg-[var(--color-secondary)]/10 text-[var(--color-secondary)]",
  "follow-up": "bg-[var(--color-accent-action-light)] text-orange-800", new: "bg-[var(--color-success)]/10 text-[var(--color-success)]",
};

function getTagDotClass(tag: ConversationTagDefinition): string {
  if (tag.color) {
    return "";
  }

  return TAG_COLORS[tag.name]?.split(" ")[0] ?? "bg-[var(--color-muted-foreground)]";
}

function getTagChipClass(tagName: string, availableTags: ConversationTagDefinition[]): string {
  const definition = availableTags.find((tag) => tag.name === tagName);
  if (definition?.color) {
    return "text-white";
  }

  return TAG_COLORS[tagName] ?? "bg-[var(--color-muted)] text-[var(--color-muted-foreground)]";
}

/**
 * Sprint 3 A3-A5, A9: Enhanced conversation list with:
 * - Assignment badges (A3)
 * - Read/unread + status management (A4)
 * - Full-text search + filters (A5)
 * - Tag filtering (A9)
 * - Keyboard nav: ↑↓ = navigate, E = close, A = assign
 */
const PAGE_SIZE = 50;

export function ConversationList({
  selectedId,
  onSelect,
  onArchive,
  filters,
  onFiltersChange,
  refreshKey = 0,
  optimisticReadConversationIds = [],
  enableMobileSwipe = false,
}: ConversationListProps) {
  const [searchQuery, setSearchQuery] = useState(filters.search ?? "");
  const [showTagFilter, setShowTagFilter] = useState(false);
  const [showAssigneeFilter, setShowAssigneeFilter] = useState(false);
  const [offset, setOffset] = useState(0);
  const [allConversations, setAllConversations] = useState<Conversation[]>([]);
  const [swipeConversationId, setSwipeConversationId] = useState<string | null>(null);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const swipeStartRef = useRef<{ id: string; x: number } | null>(null);

  // NI-3: Use ref for filters so debounce effect doesn't re-fire on every filter change
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Endpoint: GET /v1/inbox/conversations?status=...&channel=...&search=...&limit=50&offset=0
  const queryParts: string[] = [`limit=${PAGE_SIZE}`, `offset=${offset}`, `refresh=${refreshKey}`];
  if (filters.status) queryParts.push(`status=${filters.status}`);
  if (filters.channel) queryParts.push(`channel=${filters.channel}`);
  if (filters.assignee) queryParts.push(`assignee=${filters.assignee}`);
  if (filters.tag) queryParts.push(`tag=${filters.tag}`);
  if (filters.search) queryParts.push(`search=${encodeURIComponent(filters.search)}`);
  const queryString = `?${queryParts.join("&")}`;
  const { data: apiConversations } = useApi<Conversation[]>(`/v1/inbox/conversations${queryString}`);

  // NI-2: Append pages instead of replacing — reset when filters change, append when offset changes
  useEffect(() => {
    if (!apiConversations) return;
    if (offset === 0) {
      setAllConversations(apiConversations);
    } else {
      setAllConversations((prev) => [...prev, ...apiConversations]);
    }
  }, [apiConversations, offset]);

  const visibleConversations = offset === 0 && apiConversations ? apiConversations : allConversations;

  const conversations: Conversation[] = visibleConversations.map((conversation) => (
    optimisticReadConversationIds.includes(conversation.id)
      ? { ...conversation, unreadCount: 0 }
      : conversation
  ));

  // Fetch available tags from API instead of hardcoding
  const { data: apiTags } = useApi<ConversationTagDefinition[]>("/v1/inbox/tags");
  const availableTags = apiTags ?? [];
  const { data: teamMembers } = useOrganizationMembers();
  const availableAssignees = (teamMembers ?? []).map((member) => ({
    id: member.id,
    name: member.name,
    email: member.email,
  }));
  const selectedAssigneeLabel = filters.assignee
    ? availableAssignees.find((member) => member.id === filters.assignee)?.name
      ?? availableAssignees.find((member) => member.id === filters.assignee)?.email
      ?? filters.assignee
    : "Assignee";

  // Reset offset and accumulated list when filters change
  useEffect(() => {
    setOffset(0);
    setAllConversations([]);
  }, [filters.status, filters.channel, filters.assignee, filters.tag, filters.search, refreshKey]);

  // A5: Debounced search — NI-3: only depend on searchQuery and onFiltersChange, read filters from ref
  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({ ...filtersRef.current, search: searchQuery || undefined });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, onFiltersChange]);

  // Keyboard navigation
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLInputElement) return;
      const currentIndex = conversations.findIndex(c => c.id === selectedId);
      if (currentIndex < 0) return;
      const next = conversations[currentIndex + 1];
      const prev = conversations[currentIndex - 1];
      if (e.key === "ArrowDown" && next) {
        onSelect(next.id);
        e.preventDefault();
      }
      if (e.key === "ArrowUp" && prev) {
        onSelect(prev.id);
        e.preventDefault();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedId, conversations, onSelect]);

  // Server already applies filters — trust the API response, no client-side re-filtering

  const handlePointerStart = useCallback((conversationId: string, clientX: number) => {
    if (!enableMobileSwipe || !onArchive) {
      return;
    }
    swipeStartRef.current = { id: conversationId, x: clientX };
    setSwipeConversationId(conversationId);
    setSwipeOffset(0);
  }, [enableMobileSwipe, onArchive]);

  const handlePointerMove = useCallback((conversationId: string, clientX: number) => {
    const swipe = swipeStartRef.current;
    if (!swipe || swipe.id !== conversationId) {
      return;
    }

    const delta = clientX - swipe.x;
    if (delta > 0) {
      setSwipeOffset(0);
      return;
    }

    setSwipeOffset(Math.max(delta, -132));
  }, []);

  const handlePointerEnd = useCallback((conversationId: string) => {
    const shouldArchive = swipeConversationId === conversationId && swipeOffset <= -100;
    swipeStartRef.current = null;
    setSwipeConversationId(null);
    setSwipeOffset(0);
    if (shouldArchive) {
      onArchive?.(conversationId);
    }
  }, [onArchive, swipeConversationId, swipeOffset]);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-[var(--color-border)]">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Inbox</h2>
          <span className="text-xs text-[var(--color-muted-foreground)]">{conversations.length} conversations</span>
        </div>
        {/* A5: Search */}
        <div className="relative mt-2">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-muted-foreground)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search messages, names, tags..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 rounded-md border border-[var(--color-border)] text-sm text-[var(--color-foreground)] bg-[var(--color-background)] focus:border-[var(--color-primary)]/80 focus:ring-1 focus:ring-[var(--color-ring)]"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-1 px-4 py-2 border-b border-[var(--color-border)]/50">
        {/* Status filter */}
        {["all", "open", "closed", "snoozed"].map((status) => (
          <button
            key={status}
            onClick={() => onFiltersChange({ ...filters, status: status === "all" ? undefined : status })}
            className={`px-2 py-0.5 text-xs rounded-full ${
              (status === "all" && !filters.status) || filters.status === status
                ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium"
                : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
            }`}
          >
            {status}
          </button>
        ))}
        <span className="text-[var(--color-border)]">|</span>
        {/* Unified channel filter */}
        {CHANNEL_FILTERS.map((channel) => (
          <button
            key={channel.id}
            onClick={() => onFiltersChange({ ...filters, channel: filters.channel === channel.id ? undefined : channel.id })}
            className={`px-2 py-0.5 text-xs rounded-full ${
              filters.channel === channel.id
                ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-medium"
                : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
            }`}
          >
            {channel.label}
          </button>
        ))}
        <span className="text-[var(--color-border)]">|</span>
        {/* Assignment filter */}
        <div className="relative">
          <button
            onClick={() => setShowAssigneeFilter(!showAssigneeFilter)}
            className={`px-2 py-0.5 text-xs rounded-full ${
              filters.assignee
                ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
            }`}
          >
            @{selectedAssigneeLabel}
          </button>
          {showAssigneeFilter && (
            <div className="absolute top-full left-0 mt-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg shadow-lg z-10 py-1 min-w-[160px]">
              <button
                onClick={() => {
                  onFiltersChange({ ...filters, assignee: undefined });
                  setShowAssigneeFilter(false);
                }}
                className="w-full text-left px-3 py-1 text-xs hover:bg-[var(--color-muted)]"
              >
                All assignees
              </button>
              {availableAssignees.map((member) => (
                <button
                  key={member.id}
                  onClick={() => {
                    onFiltersChange({ ...filters, assignee: member.id });
                    setShowAssigneeFilter(false);
                  }}
                  className="w-full text-left px-3 py-1 text-xs hover:bg-[var(--color-muted)]"
                >
                  {member.name ?? member.email}
                </button>
              ))}
            </div>
          )}
        </div>
        <span className="text-[var(--color-border)]">|</span>
        {/* A9: Tag filter */}
        <div className="relative">
          <button
            onClick={() => setShowTagFilter(!showTagFilter)}
            className={`px-2 py-0.5 text-xs rounded-full ${
              filters.tag ? "bg-[var(--color-secondary)]/10 text-[var(--color-secondary)]" : "text-[var(--color-muted-foreground)] hover:bg-[var(--color-muted)]"
            }`}
          >
            <Tag className="w-3 h-3 inline-block" /> {filters.tag ?? "Tags"}
          </button>
          {showTagFilter && (
            <div className="absolute top-full left-0 mt-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
              <button onClick={() => { onFiltersChange({ ...filters, tag: undefined }); setShowTagFilter(false); }}
                className="w-full text-left px-3 py-1 text-xs hover:bg-[var(--color-muted)]">All tags</button>
              {availableTags.map(tag => (
                <button key={tag.name} onClick={() => { onFiltersChange({ ...filters, tag: tag.name }); setShowTagFilter(false); }}
                  className="w-full text-left px-3 py-1 text-xs hover:bg-[var(--color-muted)]">
                  <span
                    className={`inline-block w-2 h-2 rounded-full mr-1 ${getTagDotClass(tag)}`}
                    style={tag.color ? { backgroundColor: tag.color } : undefined}
                  />
                  {tag.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Conversation List */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="p-8 text-center text-[var(--color-muted-foreground)] text-sm">
            <p>{filters.search ? "No matching conversations" : "No conversations yet"}</p>
            {!filters.search && <p className="mt-1 text-xs">Inbound messages will appear here</p>}
          </div>
        ) : (
          conversations.map((conv) => (
            <div key={conv.id} className="relative overflow-hidden border-b border-[var(--color-border)]/50">
              {enableMobileSwipe && onArchive ? (
                <div className="absolute inset-y-0 right-0 flex w-28 items-center justify-center bg-[var(--color-warning)]/10 text-xs font-semibold text-[var(--color-warning)]">
                  Swipe to close
                </div>
              ) : null}
              <button
                onClick={() => onSelect(conv.id)}
                onPointerDown={(event) => handlePointerStart(conv.id, event.clientX)}
                onPointerMove={(event) => handlePointerMove(conv.id, event.clientX)}
                onPointerUp={() => handlePointerEnd(conv.id)}
                onPointerCancel={() => handlePointerEnd(conv.id)}
                className={`w-full text-left px-4 py-3 hover:bg-[var(--color-muted)] transition-colors ${
                  selectedId === conv.id ? "bg-[var(--color-primary)]/5 bg-[var(--color-primary)]/10" : ""
                } ${conv.unreadCount > 0 ? "font-medium" : ""}`}
                style={{
                  transform: swipeConversationId === conv.id ? `translateX(${swipeOffset}px)` : undefined,
                  transition: swipeConversationId === conv.id ? "none" : "transform 160ms ease",
                }}
              >
                <div className="flex items-start gap-3">
                  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm ${CHANNEL_COLORS[conv.channel] ?? "bg-[var(--color-muted)]"}`}>
                    {CHANNEL_ICONS[conv.channel] ?? <MessageCircle className="w-4 h-4" />}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="flex min-w-0 items-center gap-2">
                        {conv.unreadCount > 0 ? (
                          <span
                            aria-label="Unread conversation"
                            className="inline-flex h-2 w-2 flex-shrink-0 rounded-full bg-[var(--color-primary)]"
                          />
                        ) : null}
                        <span className={`text-sm truncate ${conv.unreadCount > 0 ? "font-semibold text-[var(--color-foreground)]" : "text-[var(--color-foreground)]"}`}>
                          {conv.personName}
                        </span>
                      </span>
                      <span className="text-xs text-[var(--color-muted-foreground)] flex-shrink-0 ml-2">
                        {formatTime(conv.lastMessageAt)}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--color-muted-foreground)] truncate mt-0.5">{conv.lastMessage}</p>
                    <div className="flex items-center gap-1 mt-1">
                      {conv.tags.map(tag => {
                        const definition = availableTags.find((item) => item.name === tag);
                        return (
                        <span
                          key={tag}
                          className={`inline-flex px-1.5 py-0 text-[10px] rounded-full ${getTagChipClass(tag, availableTags)}`}
                          style={definition?.color ? { backgroundColor: definition.color } : undefined}
                        >
                          {tag}
                        </span>
                        );
                      })}
                      {conv.assignedTo && (
                        <span className="text-[10px] text-[var(--color-muted-foreground)] ml-auto">@{conv.assignedTo}</span>
                      )}
                    </div>
                  </div>
                  {conv.unreadCount > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-[var(--color-primary)] rounded-full flex-shrink-0">
                      {conv.unreadCount > 9 ? "9+" : conv.unreadCount}
                    </span>
                  )}
                </div>
              </button>
            </div>
          ))
        )}
        {(apiConversations?.length ?? 0) >= PAGE_SIZE && (
          <button
            onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
            className="w-full py-3 text-xs text-[var(--color-primary)] hover:bg-[var(--color-muted)] transition-colors border-t border-[var(--color-border)]/50"
          >
            Load more conversations
          </button>
        )}
      </div>
    </div>
  );
}
